<template>
    <div>
      <div style="display:flex; gap:8px;">
        <app-autocomplete
          v-model="countryCode"
          :items="phoneCountries"
          :text_field="'display'"
          :value_field="'code'"
          :errors="errors && errors.country"
          style="flex:1"
          @change="emitIfValid"
        />
        <input
          type="text"
          v-model="nationalNumber"
          class="n2-input-default"
          :class="{'n2-invalid-field': errors && errors.national}"
          style="flex:2"
          @blur="emitIfValid"
          @keyup.enter="emitIfValid"
        />
      </div>
  
      <ul
        class="n2-valid"
        v-if="errors && (errors.country || errors.national)"
        :class="{'n2-errors': true}"
      >
        <li v-for="(msg, i) in allErrors" :key="i">{{ msg }}</li>
      </ul>
  
      <div v-if="preview" class="n2-text-muted" style="margin-top:4px;color:green;">
         {{ preview }}
      </div>
    </div>
  </template>
  
  <script>
  import { parsePhoneNumberFromString, getCountryCallingCode } from 'libphonenumber-js/max'
  // 如需更严格校验可改为：'libphonenumber-js/max'
  import i18nCountries from 'i18n-iso-countries'
  import en from 'i18n-iso-countries/langs/en.json'
  import zh from 'i18n-iso-countries/langs/zh.json'
  import nb from 'i18n-iso-countries/langs/nb.json'
  import AppAutocomplete from '../shared/autocomplete.vue'
  
  i18nCountries.registerLocale(en)
  i18nCountries.registerLocale(zh)
  i18nCountries.registerLocale(nb)
  
  export default {
    name: 'PhoneNumberField',
    components: { AppAutocomplete },
  
    props: {
      value: { type: String, default: '' },            // v-model: E.164 或本地号（兼容）
      defaultCountry: { type: String, default: 'NO' }, // 默认国家(ISO alpha-2)
      locale: { type: String, default: 'en' },         // 'en'|'zh'|'nb'...
      previewLabel: { type: String, default: 'Formatted' },
      errors: { type: Object, default: null },         // { country?: string[], national?: string[] }
      autoNormalizeOnMount: { type: Boolean, default: false } // 首次渲染后尝试标准化写回
    },
  
    data () {
      return {
        countryCode: this.defaultCountry,
        nationalNumber: '',
        touched: false
      }
    },
  
    computed: {
      allErrors () {
        const arr = []
        if (this.errors?.country) arr.push(...this.errors.country)
        if (this.errors?.national) arr.push(...this.errors.national)
        return arr
      },
  
      phoneCountries () {
        const lang = this.mapLocaleToIso(this.locale)
        const names = i18nCountries.getNames(lang) || i18nCountries.getNames('en')
        const items = []
        for (const code in names) {
          try {
            const dial = '+' + getCountryCallingCode(code)
            items.push({ code, name: names[code], dial, display: `(${dial}) ${names[code]}` })
        } catch (_) {
  // ignore: some regions may not have calling code
}
        }
        return items.sort((a, b) => a.name.localeCompare(b.name))
      },
  
      preview () {
        if (!this.nationalNumber || !this.countryCode) return ''
        const p = parsePhoneNumberFromString(this.nationalNumber, this.countryCode)
        return p && p.isValid() ? p.formatInternational() : ''
      }
    },
  
    watch: {
      // 父组件传入的值：支持 E.164(+开头) 或本地号（不带+）
      value: {
        immediate: true,
        handler (val) {
          if (val == null) return
          const raw = String(val).trim()
          if (!raw) {
            this.nationalNumber = ''
            return
          }
          if (raw.startsWith('+')) {
            // E.164：解析拆回
            const p = parsePhoneNumberFromString(raw)
            if (p && p.isValid()) {
              this.countryCode = p.country || this.countryCode || this.defaultCountry
              this.nationalNumber = p.nationalNumber || ''
            } else {
              // 非法 E.164，清空展示
              this.nationalNumber = ''
            }
          } else {
            // 本地号：直接展示；国家取当前/默认
            if (!this.countryCode) this.countryCode = this.defaultCountry
            this.nationalNumber = raw
          }
  
          if (this.autoNormalizeOnMount) {
            this.$nextTick(this.emitIfValid)
          }
        }
      },
  
      countryCode () {
        this.touched = true
      }
    },
  
    methods: {
      mapLocaleToIso (l) {
        if (!l) return 'en'
        const ll = l.toLowerCase()
        if (ll === 'no' || ll.startsWith('nb')) return 'nb'
        if (ll.startsWith('zh')) return 'zh'
        return 'en'
      },
  
      emitIfValid () {
        const raw = (this.nationalNumber || '').trim()
        if (!raw || !this.countryCode) {
          this.$emit('input', '')
          return
        }
        const p = parsePhoneNumberFromString(raw, this.countryCode)
        if (p && p.isValid()) {
          // 回填标准化 E.164
          this.$emit('input', p.number)
          // 同步拆分信息（父组件可选使用）
          this.$emit('change-breakdown', {
            country_code: p.country || this.countryCode,
            calling_code: p.countryCallingCode,
            national_number: p.nationalNumber,
            e164: p.number
          })
        } else {
          // 不合法则清空 v-model
          this.$emit('input', '')
        }
      }
    }
  }
  </script>